import React from 'react';
import { Ticket } from '../types';

interface TicketInfoProps {
  ticket: Ticket;
  onUseTicket: () => void;
  onScanAnother: () => void;
  isLoading: boolean;
  isResettingScanner: boolean;
}

const TicketInfo: React.FC<TicketInfoProps> = ({
  ticket,
  onUseTicket,
  onScanAnother,
  isLoading,
  isResettingScanner,
}) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
      <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">Ticket Information</h2>
      
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Ticket Information */}
            <div>
              <label className="text-sm font-medium text-gray-600">Ticket Number</label>
              <p className="text-sm text-gray-900 font-mono">{ticket.number}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Ticket Type</label>
              <p className="text-sm text-gray-900">{ticket.type || 'General'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Price</label>
              <p className="text-sm text-gray-900 font-semibold">€{typeof ticket.price === 'number' ? ticket.price.toFixed(2) : ticket.price}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Date & Time</label>
              <p className="text-sm text-gray-900">
                {ticket.date ? new Date(ticket.date).toLocaleDateString() : 'N/A'} at {ticket.time || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Status</label>
              <div className="space-y-1">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  ticket.status === 'used' 
                    ? 'bg-red-100 text-red-800' 
                    : ticket.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : ticket.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {ticket.status === 'used' ? 'Used' : 
                   ticket.status === 'active' ? 'Available' :
                   ticket.status === 'pending' ? 'Pending Payment' :
                   ticket.status}
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Created</label>
              <p className="text-sm text-gray-900">
                {ticket.created_at ? new Date(ticket.created_at).toLocaleString() : 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Updated</label>
              <p className="text-sm text-gray-900">
                {ticket.updated_at ? new Date(ticket.updated_at).toLocaleString() : 'N/A'}
              </p>
            </div>
            
            {/* QR Code */}
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-600">QR Code</label>
              <div className="mt-2">
                {ticket.qr_code ? (
                  <div 
                    className="w-32 h-32 border border-gray-300 rounded overflow-hidden"
                    dangerouslySetInnerHTML={{ __html: ticket.qr_code }}
                  />
                ) : (
                  <div className="w-32 h-32 bg-gray-200 border border-gray-300 rounded flex items-center justify-center">
                    <span className="text-xs text-gray-500">No QR Code</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          {ticket.status === 'active' && (
            <button
              onClick={onUseTicket}
              disabled={isLoading}
              className={`flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 text-sm font-medium ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? 'Processing...' : 'Mark as Used'}
            </button>
          )}
          
          {ticket.status === 'pending' && (
            <div className="flex-1 bg-yellow-50 border border-yellow-200 text-yellow-800 py-2 px-4 rounded-lg text-sm font-medium text-center">
              Cannot mark as used - payment pending
            </div>
          )}
          
          {ticket.status === 'used' && (
            <div className="flex-1 bg-red-50 border border-red-200 text-red-800 py-2 px-4 rounded-lg text-sm font-medium text-center">
              Ticket already used
            </div>
          )}
          
          <button
            onClick={onScanAnother}
            disabled={isResettingScanner}
            className={`flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 text-sm font-medium ${
              isResettingScanner ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isResettingScanner ? 'Resetting...' : 'Scan Another Ticket'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketInfo;
